import { defineStore } from 'pinia'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

import type { EntryPoint } from '../composables/useShoppingAssistantContext'

export interface ShoppingAssistantWebSidebarState {
  // Whether the floating/full-screen assistant is shown.
  isOpen: boolean
  // Which CTA opened the assistant. Required — callers must declare intent.
  entryPoint: EntryPoint | null
  // Seed consumed once by the chat core when it mounts on a fresh open.
  seededMessage: string
  pageContext: ChatRequestPageContext | null
  // Incremented on each restart() so the host can key ShoppingAssistantChat
  // and force a remount without closing the panel.
  conversationKey: number
}

export interface OpenOptions {
  entryPoint: EntryPoint
  message?: string
  pageContext?: ChatRequestPageContext | null
}

/**
 * Presence state for the web sidebar shopping assistant: open/closed plus the
 * seed handoff from entry points. It is the single seam the header triggers
 * and the web-sidebar host reads. Persists nothing (v1).
 */
export const useShoppingAssistantWebSidebar = defineStore(
  'shoppingAssistantWebSidebar',
  {
    state: (): ShoppingAssistantWebSidebarState => ({
      isOpen: false,
      entryPoint: null,
      seededMessage: '',
      pageContext: null,
      conversationKey: 0,
    }),
    actions: {
      open({ entryPoint, message = '', pageContext = null }: OpenOptions) {
        this.isOpen = true
        this.entryPoint = entryPoint
        this.seededMessage = message
        this.pageContext = pageContext
      },
      // Replace the live conversation without closing the panel. The host keys
      // ShoppingAssistantChat on conversationKey, so incrementing it forces a
      // remount without triggering the panel open/close transition.
      restart({ entryPoint, message = '', pageContext = null }: OpenOptions) {
        this.conversationKey += 1
        this.entryPoint = entryPoint
        this.seededMessage = message
        this.pageContext = pageContext
      },
      close() {
        this.isOpen = false
        this.entryPoint = null
        this.seededMessage = ''
        this.pageContext = null
      },
    },
  },
)
